import { Button } from "@/components/flexkit/Button";
import { Tooltip } from "@/components/Tooltip";
import { useMemo, useEffect, forwardRef } from "react";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useListMCPIntegrations } from "@/queries/integration-queries";
import { useListHostAuthenticationTokens } from "@/queries/runner-configuration-queries";
import { IconIntegrations } from "@/assets/icons/geist/IconIntegrations";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { AlertTriangle } from "lucide-react";
import { isErrorSuppressed, type MCPErrorSnapshot } from "@/queries/integration-queries";

type MCPIntegrationsButtonProps = {
    agentExecution: PlainAgentExecution;
    organizationId?: string;
    errorSnapshots: Map<string, MCPErrorSnapshot>;
} & PropsWithClassName;

export const MCPIntegrationsButton = forwardRef<HTMLButtonElement, MCPIntegrationsButtonProps>(
    function MCPIntegrationsButton({ className, agentExecution, organizationId, errorSnapshots, ...rest }, ref) {
        const { data: integrations } = useListMCPIntegrations(organizationId);
        const { data: authTokensData } = useListHostAuthenticationTokens({ enabled: true });
        // Snapshot the statusVersion and authTokenId for each integration with errors.
        // Only update when statusVersion changes so we can detect stale errors after re-auth.
        const statusVersion = String(agentExecution.status?.statusVersion ?? "0");
        useEffect(() => {
            if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return;

            const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
            const tokensMap = new Map(
                authTokensData.tokens.filter((t) => t.integrationId).map((t) => [t.integrationId, t]),
            );

            for (const integration of integrations) {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                const hasError = !!(status?.failureMessage || status?.warningMessage);
                if (hasError) {
                    const existing = errorSnapshots.get(integration.id);
                    if (existing?.statusVersion !== statusVersion) {
                        errorSnapshots.set(integration.id, {
                            statusVersion,
                            authTokenId: tokensMap.get(integration.id)?.id,
                        });
                    }
                }
            }
        }, [
            integrations,
            authTokensData,
            agentExecution.status?.mcpIntegrationStatuses,
            statusVersion,
            errorSnapshots,
        ]);

        // Count integrations that have auth tokens and non-suppressed errors/warnings.
        // Pure computation — no side effects.
        const issueCount = useMemo(() => {
            if (!integrations || !authTokensData || !agentExecution.status?.mcpIntegrationStatuses) return 0;

            const mcpStatuses = agentExecution.status.mcpIntegrationStatuses;
            const tokensMap = new Map(
                authTokensData.tokens.filter((t) => t.integrationId).map((t) => [t.integrationId, t]),
            );

            return integrations.filter((integration) => {
                const status = mcpStatuses.find((s) => s.id === integration.id);
                const hasAuthToken = tokensMap.has(integration.id);
                const hasError = !!(status?.failureMessage || status?.warningMessage);
                if (!hasAuthToken || !hasError) return false;

                // Hack: Suppress stale errors: authToken changed but statusVersion hasn't
                if (
                    isErrorSuppressed(
                        errorSnapshots.get(integration.id),
                        statusVersion,
                        tokensMap.get(integration.id)?.id,
                    )
                ) {
                    return false;
                }

                return true;
            }).length;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [integrations, authTokensData, agentExecution.status?.mcpIntegrationStatuses, statusVersion]);

        return (
            <Tooltip content="MCP Integrations">
                <div className="relative flex items-center">
                    <Button
                        ref={ref}
                        variant="ghost"
                        LeadingIcon={IconIntegrations}
                        className={cn(
                            "flex items-center justify-center rounded-lg p-2 text-content-secondary hover:bg-surface-button-clear-accent hover:text-content-accent disabled:cursor-not-allowed disabled:opacity-50",
                            className,
                        )}
                        aria-label="MCP Integrations"
                        data-tracking-id="mcp-integrations-button"
                        {...rest}
                    />
                    {issueCount > 0 && (
                        <div className="flex items-center gap-0.5 text-yellow-600 dark:text-yellow-400">
                            <AlertTriangle size={14} />
                            <span className="text-xs font-medium">{issueCount}</span>
                        </div>
                    )}
                </div>
            </Tooltip>
        );
    },
);
